import { string, z } from 'zod';
import { Channels, Intents } from './message-classification-metadata.schema';

// ── Shared Enums ────────────────────────────────────────────────────────────────
export const ChannelEnum = z.enum([
    'LINKEDIN',
    'EMAIL',
    'TWITTER',
    'WHATSAPP',
    'SLACK',
    'TEAMS',
    'SMS',
    'PHONE',
    'IN_PERSON',
    'ZOOM',
    'GOOGLE_MEET',
]);

export const Roles = [
    'FRIEND',
    'FAMILY',
    'PROSPECT',
    'CUSTOMER',
    'FORMER_CUSTOMER',
    'COLLEAGUE',
    'FORMER_COLLEAGUE',
    'INVESTOR',
    'HIRING_CANDIDATE',
    'RECRUITER',
    'VENDOR',
    'SUPPLIER',
    'SELLER',
    'ADVISOR',
    'INFLUENCER',
    'REFERRAL_SOURCE',
    'END_USER',
    'COMPETITOR',
    'INDUSTRY_PEER',
    'INDUSTRY_EXPERT',
    'CUSTOMER_SUPPORT',
    'OTHER',
] as const;

// Simplified and MECE intent categories
export const IntentEnum = z.enum([
    // Personal & Social
    'PERSONAL_SOCIAL', // Personal conversation, staying in touch, friendship

    // Business Development
    'SALES', // Selling products/services
    'PARTNERSHIP', // Business partnerships, joint ventures
    'FUNDRAISING', // Raising capital, investor relations

    // Customer Management
    'CUSTOMER_SUCCESS', // Onboarding, support, retention
    'RENEWAL_EXPANSION', // Contract renewal, upsell, cross-sell

    // Talent & Operations
    'HIRING', // Recruiting, hiring
    'NETWORKING', // Professional networking, relationship building
    'SUPPORT_SERVICE', // Technical support, service delivery

    // Information & Marketing
    'INFORMATION_GATHERING', // Research, market intelligence
    'MARKETING_PROMOTION', // Content sharing, event promotion
    'REFERRAL_INTRODUCTION', // Making or requesting introductions

    'OTHER',
]);

// Consolidated and MECE stage progression
export const StageEnum = z.enum([
    // Initial Contact
    'INITIAL_CONTACT', // First interaction, cold outreach, inbound inquiry

    // Early Stage
    'AWARENESS_INTEREST', // Showing interest, asking questions
    'QUALIFICATION', // Qualifying needs, fit, timing
    'DISCOVERY', // Deep dive into requirements, needs analysis

    // Mid Stage
    'EVALUATION', // Comparing options, technical evaluation
    'PROPOSAL_NEGOTIATION', // Proposal sent, terms discussion
    'DECISION_PENDING', // Decision being made, waiting for approval

    // Late Stage
    'COMMITMENT', // Verbal agreement, moving forward
    'CONTRACT_FINALIZATION', // Legal, contracts, final details
    'IMPLEMENTATION', // Onboarding, delivery, execution

    // Ongoing Relationship
    'ACTIVE_RELATIONSHIP', // Ongoing customer/partner relationship
    'EXPANSION_RENEWAL', // Renewal discussions, expansion opportunities

    // End States
    'CLOSED_WON', // Successfully closed, deal won
    'CLOSED_LOST', // Deal lost, opportunity closed
    'DORMANT', // Inactive, no current activity

    'OTHER',
]);

export const DecisionTimeframeEnum = z.enum([
    'IMMEDIATE', // Within days
    'SHORT_TERM', // 1-4 weeks
    'MEDIUM_TERM', // 1-3 months
    'LONG_TERM', // 3+ months
    'UNDETERMINED', // Timeline unclear
    'DEPENDENT_ON_EVENT', // Waiting for external trigger
]);

export const ConfidenceLevel = z.enum(['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH']);

export const EngagementTrendEnum = z.enum(['INCREASING', 'DECREASING', 'STABLE', 'INCONSISTENT']);

export const SentimentEnum = z.enum([
    'VERY_POSITIVE', // Enthusiastic, excited
    'POSITIVE', // Interested, satisfied, appreciative
    'NEUTRAL', // Neutral, matter-of-fact
    'NEGATIVE', // Concerned, frustrated, disappointed
    'VERY_NEGATIVE', // Angry, very dissatisfied
    'MIXED', // Contains both positive and negative sentiment
]);

// Consolidated and MECE objections
export const ObjectionEnum = z.enum([
    // Financial
    'PRICE_BUDGET', // Price too high, budget constraints

    // Timing & Priority
    'TIMING_PRIORITY', // Wrong timing, other priorities

    // Product & Technical
    'PRODUCT_FIT', // Product doesn't meet needs, feature gaps
    'TECHNICAL_CONCERNS', // Implementation complexity, integration issues

    // Organizational
    'INTERNAL_PROCESS', // Approval process, internal resistance, decision-maker access
    'RESOURCE_CONSTRAINTS', // Lack of resources, expertise required

    // Market & Competition
    'COMPETITIVE_ALTERNATIVE', // Prefer competitor, existing vendor relationship
    'RISK_CONCERNS', // Risk aversion, ROI concerns, compliance issues

    // Need & Readiness
    'NO_IMMEDIATE_NEED', // Don't see the need, not ready to change
]);

// Consolidated and MECE opportunities
export const OpportunityEnum = z.enum([
    // Growth Opportunities
    'NEW_BUSINESS', // New customer opportunity
    'EXPANSION', // Upsell, cross-sell, expansion within account
    'RENEWAL', // Contract renewal opportunity

    // Engagement Signals
    'DEMO_TRIAL_INTEREST', // Wants demo, trial, or pilot
    'PRICING_PROPOSAL_REQUEST', // Requesting pricing or formal proposal

    // Partnership & Advocacy
    'PARTNERSHIP_COLLABORATION', // Partnership, joint venture, collaboration
    'REFERRAL_ADVOCACY', // Referral opportunity, testimonial, case study

    // Service & Consulting
    'PROFESSIONAL_SERVICES', // Consulting, implementation, training services
]);

// Simplified and MECE risk indicators
export const RiskIndicators = [
    'CUSTOMER_CHURN_RISK',
    'LOW_ENGAGEMENT_RESPONSE_RATE',
    'CUSTOMER_DISSATISFACTION_COMPLAINTS',
    'EVALUATING_COMPETITORS',
    'KEY_STAKEHOLDER_TURNOVER',
    'CUSTOMER_BUDGET_CUTS',
    'CUSTOMER_ORGANIZATIONAL_CHANGES',
    'CUSTOMER_NEGATIVE_PUBLICITY',
    'PROJECT_IMPLEMENTATION_DELAY',
    'UNRESOLVED_CUSTOMER_SUPPORT_ISSUES',
    'CUSTOMER_PAYMENT_ISSUES',
    'OTHER_RISK',
    'NO_IDENTIFIED_RISKS',
] as const;

export const PriorityEnum = z.enum(['HIGH', 'MEDIUM', 'LOW']);

export const Sincerity = ['VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW', 'VERY_LOW'] as const;

export const Sentiment = [
    'VERY_POSITIVE', // Enthusiastic, excited
    'POSITIVE', // Interested, satisfied, appreciative
    'NEUTRAL', // Neutral, matter-of-fact
    'NEGATIVE', // Concerned, frustrated, disappointed
    'VERY_NEGATIVE', // Angry, very dissatisfied
    'MIXED', // Contains both positive and negative sentiment
] as const;

// Simplified and MECE next steps
export const NextStepCodeEnum = z.enum([
    'RESPOND', // Reply to message, answer question
    'FOLLOW_UP', // Follow up on previous conversation
    'SCHEDULE', // Schedule meeting, call, or demo
    'SEND_INFORMATION', // Send proposal, quote, case study, or other materials
    'INTERNAL_ACTION', // Internal coordination, escalation, preparation
    'WAIT', // Wait for their response or external event
    'CLOSE_OPPORTUNITY', // Opportunity completed or should be closed
    'NONE', // No action needed
]);

export const PreferredFrequencies = [
    'DAILY',
    'MULTIPLE_TIMES_WEEKLY',
    'WEEKLY',
    'BI_WEEKLY',
    'MONTHLY',
    'QUARTERLY',
    'AS_NEEDED',
    'INFREQUENT',
    'ON_MILESTONES',
    'URGENT_ONLY',
] as const;

export const ConversationMetadataComputedFieldsSchema = z.object({
    numberOfTotalMessages: z.number().describe('Number of messages in the conversation'),
    numberOfLinkedinMessages: z.number().describe('Number of messages sent on LinkedIn'),
    numberOfEmailMessages: z.number().describe('Number of messages sent on Email'),

    numberOfUserMessages: z.number().describe('Number of messages sent by the user'),
    numberOfContactMessages: z.number().describe('Number of messages sent by the prospect'),

    numberOfMessagesSentInLast30Days: z.number().describe('Number of messages sent in the last 30 days'),
    numberOfMessagesSentInLast7Days: z.number().describe('Number of messages sent in the last 7 days'),

    lastMessageInConversationSentOn: z.date().nullable().describe('When the last message in the conversation was sent'),

    userToContactMessageRatio: z
        .number()
        .describe('Ratio of amount of user messages sent to contact messages sent. Rolling 50 messages.'),
});

export const ConversationMetadataGeneratedFieldsSchema = z.object({
    language: z
        .string()
        .describe(
            'The dominant language used throughout the conversation (e.g. English, Swedish, German, Spanish, Vietnamese, Mandarin, Japanese, Emoji, Unknown etc.).',
        ),

    topics: z.array(z.string()).describe('Main topics discussed across the conversation.'),
    overallSentiment: SentimentEnum.describe('General emotional tone of the conversation'),
    nextSuggestedUserActionInDaysFromLastMessage: z
        .number()
        .nullable()
        .describe(
            'Number of days after the last message before the user is suggested or expected to take the next action (reply, follow up, etc.) in the conversation. Null if not applicable.',
        ),

    userPerspective: z.object({
        intents: z.array(z.enum(Intents)).describe('The intents the person has in this conversation. '),
        sentiment: z.enum(Sentiment).describe('The sentiment of the person in this conversation.'),
        sincerity: z.enum(Sincerity).describe('The sincerity of the person in this conversation.'),
        role: z.enum(Roles).describe('The role of the person in this conversation.'),
        perceivedWarningSigns: z
            .array(z.enum(RiskIndicators))
            .optional()
            .describe(
                'Warning signs or concerns indicating disengagement, dissatisfaction, or risk of churn identified in the conversation in sales conversations. If no warning signs are identified, return an empty array.',
            ),
        perceivedWarningDescription: z
            .string()
            .optional()
            .describe(
                'A description of the warning signs or concerns indicating disengagement, dissatisfaction, or risk of churn identified in the conversation. Only add if perceivedWarningSigns is not empty.',
            ),
        perceivedObjections: z
            .array(z.string())
            .optional()
            .describe(
                'A description of the objections, pushbacks or dissatisfactions expressed by the participant during the conversation. If no objections are identified, return an empty string.',
            ),
    }),
    contactPerspective: z.object({
        intents: z.array(z.enum(Intents)).describe('The intents the person has in this conversation. '),
        sentiment: z.enum(Sentiment).describe('The sentiment of the person in this conversation.'),
        sincerity: z.enum(Sincerity).describe('The sincerity of the person in this conversation.'),
        role: z.enum(Roles).describe('The role of the person in this conversation.'),
        expectedResponseInDaysFromLastMessage: z
            .number()
            .nullable()
            .describe(
                "The number of days before the contact expects the user to respond to the contact's latest message. Null if not applicable.",
            ),
    }),

    conversationSummary: z
        .string()
        .describe('An overall description of what the conversation is about. (3-5 sentences)'),
    confidenceScore: ConfidenceLevel.describe('Overall classifier confidence in the conversation-level output'),
});

export const ConversationMetadataSchema = ConversationMetadataComputedFieldsSchema.merge(
    ConversationMetadataGeneratedFieldsSchema,
);

export type ConversationClassificationMetadata = z.infer<typeof ConversationMetadataSchema>;
